import React from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
// components
import { FormattedMessage } from 'react-intl';
import Button from 'components/Button';
import Preloader from 'components/Preloader';
import ExtendedHeader from 'components/DailyCheckups/ExtendedHeader/ExtendedHeader';
// utils
import { isMobile } from 'react-device-detect';
import cn from 'classnames';
import isEmpty from 'lodash.isempty';
import mixpanel from 'mixpanel-browser';
// styles
import './CheckupSegment.scss';

const CheckupSegment = ({
  className,
  isReported,
  itemsCount,
  titleKey,
  titleKeyWhenReported,
  isItemsCountHidden,
  isStatusIconHidden = false,
  reportPageUrl = null,
  reportBtnClassName,
  isButtonsHidden,
  onEmptyDataSubmit,
  children,
  withoutBottomBorder,
  itemsCountPlaceholder = 0,
  noData,
  isSegmentConditionChanged,
  isDisabled = false,
  isSubmitting = false,
  extendedHeaderData = {},
  title,
  warningText,
  onEdit,
  onDataSubmit,
  isRequired = false,
  currentUser,
}, { router }) => {
  const isMobileEmptySegment = isMobile && isReported && !itemsCount;
  const messageId = isReported && titleKeyWhenReported ? titleKeyWhenReported : titleKey;

  const handleYesClick = () => {
    if (currentUser && currentUser.id) {
      mixpanel.track('Feature Used', {
        user_id: currentUser.id,
        feature_name: 'CheckupSegment',
        action: 'Click Yes',
        segment_title: title || titleKey,
        timestamp: new Date().toISOString()
      });
    }

    if (onDataSubmit) {
      onDataSubmit();
    } else {
      router.push(reportPageUrl);
    }
  };

  const handleNoClick = () => {
    if (currentUser && currentUser.id) {
      mixpanel.track('Feature Used', {
        user_id: currentUser.id,
        feature_name: 'CheckupSegment',
        action: 'Click No',
        segment_title: title || titleKey,
        timestamp: new Date().toISOString()
      });
    }

    if (onEmptyDataSubmit) {
      onEmptyDataSubmit();
    }
  };

  const handleEditClick = () => {
    if (currentUser && currentUser.id) {
      mixpanel.track('Feature Used', {
        user_id: currentUser.id,
        feature_name: 'CheckupSegment',
        action: 'Click Edit',
        segment_title: title || titleKey,
        timestamp: new Date().toISOString()
      });
    }

    if (onEdit) {
      onEdit();
    } else {
      router.push(reportPageUrl);
    }
  };

  return (
    <div
      className={cn(className, 'checkup-segment', {
        'reported': isReported,
        'is-disabled': isDisabled,
        'without-bottom-border': withoutBottomBorder,
        'no-data': noData,
        'has-warning': !!warningText
      })}
    >
      <Preloader className="segment-preloader" isActive={isSubmitting} />
      {!isEmpty(extendedHeaderData) && (
        <ExtendedHeader
          headerData={extendedHeaderData}
        />
      )}
      <div className={cn('checkup-segment-content', { 'mobile-empty-segment': isMobileEmptySegment })}>
        {isReported && !isStatusIconHidden && (
          <div
            className={cn('segment-icon-column', { 'animated fadeInLeft': isReported && isSegmentConditionChanged })}
          >
            <i className={cn('fa fa-check-circle-bts', 'status-icon')} />
          </div>
        )}

        <div className={cn('segment-info-column', { isReported })}>
          <div className={cn('segment-title', { isMobile })}>
            {title || <FormattedMessage id={messageId} />}
            {isRequired && (
              <span className="required-indicator" title={<FormattedMessage id="general.required" />}>
                * <span className="required-label">({<FormattedMessage id="general.required" />})</span>
              </span>
            )}
            {!isItemsCountHidden && isReported &&
              <span className="value"> • {itemsCount || itemsCountPlaceholder}</span>
            }
          </div>
          {warningText && (
            <div className="warning-text hide-for-large">
              <i className="fa fa-exclamation-triangle-bts warning-icon" />
              {warningText}
            </div>
          )}
        </div>
        {!isButtonsHidden && (
          <div className="segment-buttons-column">
            {!isReported && (
              <Button
                light
                className="action-button"
                onClick={handleNoClick}
                disabled={isSubmitting || isRequired}
                title={isRequired ? <FormattedMessage id="general.requiredField" /> : undefined}
              >
                <FormattedMessage id="general.button.no" />
              </Button>
            )}
            {!isReported &&  (
              <Button
                light
                disabled={isSubmitting}
                onClick={handleYesClick}
                className={cn('action-button', reportBtnClassName)}
              >
                <FormattedMessage id="general.button.yes" />
              </Button>
            )}
            {isReported && (
              <Button
                light
                onClick={handleEditClick}
                to={reportPageUrl}
                className={cn({
                  'mobile-edit-btn': isMobile,
                  'animated fadeInLeft': isSegmentConditionChanged
                })}
              >
                <FormattedMessage id="general.button.edit" />
              </Button>
            )}
          </div>
        )}
      </div>
      {warningText && (
        <div className="warning-segment">
          <i className="fa fa-exclamation-triangle-bts warning-icon" />
          {warningText}
        </div>
      )}
      {isReported && children}
    </div>
  );
};

CheckupSegment.contextTypes = {
  router: T.object,
};

CheckupSegment.propTypes = {
  className: T.string,
  itemsCount: T.number,
  titleKey: T.string,
  title: T.oneOfType([T.string, T.node]),
  warningText: T.oneOfType([T.string, T.node]),
  titleKeyWhenReported: T.string,
  isItemsCountHidden: T.bool,
  isStatusIconHidden: T.bool,
  reportPageUrl: T.string,
  reportBtnClassName: T.string,
  isButtonsHidden: T.bool,
  isReported: T.bool.isRequired,
  children: T.node,
  onEmptyDataSubmit: T.func,
  withoutBottomBorder: T.bool,
  itemsCountPlaceholder: T.oneOfType([T.string, T.number, T.node]),
  noData: T.bool,
  onDataSubmit: T.func,
  onEdit: T.func,
  isSegmentConditionChanged: T.bool,
  isSubmitting: T.bool,
  isDisabled: T.bool,
  isRequired: T.bool,
  extendedHeaderData: T.shape({
    isError: T.bool,
    text: T.oneOfType([T.string, T.object]),
  }),
  currentUser: T.object,
};

export default connect(
  (state) => ({
    currentUser: state.auth.user,
  })
)(CheckupSegment);
